import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  ArrowLeftIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  getStudentById,
  students,
  JASH_ID,
  jashReadingTime,
  jashSubjects,
  subjectColors,
  jashStruggleThemes,
  jashOverconfidence,
  jashStuckConcepts,
  jashAISummary,
  type StruggleTheme,
} from '../data/mockAnalyticsData';

// ─── Section Card ────────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  badge?: React.ReactNode;
}> = ({ title, icon, children, badge }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`rounded-2xl border p-6 ${
      isDarkMode ? 'bg-navy-800/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={isDarkMode ? 'text-frost-400' : 'text-frost-600'}>{icon}</span>
          <h3 className={`text-sm font-medium uppercase tracking-wider ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>{title}</h3>
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
};

// ─── Struggle Theme Row ──────────────────────────────────────────────────────

const StruggleRow: React.FC<{ theme: StruggleTheme }> = ({ theme }) => {
  const { isDarkMode } = useTheme();
  const statusConfig = {
    struggling: { label: 'Still struggling', color: 'text-red-400', bg: isDarkMode ? 'bg-red-500/10' : 'bg-red-50', dot: 'bg-red-400' },
    unverified: { label: 'Asked, unverified', color: 'text-yellow-400', bg: isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50', dot: 'bg-yellow-400' },
    mastered: { label: 'Mastered', color: 'text-green-400', bg: isDarkMode ? 'bg-green-500/10' : 'bg-green-50', dot: 'bg-green-400' },
  };
  const cfg = statusConfig[theme.status];

  return (
    <div className={`rounded-xl p-4 ${cfg.bg}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
            <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {theme.concept}
            </span>
          </div>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {theme.chapter}
          </p>
        </div>
        <div className="text-right">
          <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {theme.questionsThisWeek} questions this week
            {theme.fsrsState && ` · ${theme.fsrsState}`}
          </p>
        </div>
      </div>
      <p className={`text-sm italic pl-4 border-l-2 ${
        isDarkMode ? 'text-gray-400 border-white/10' : 'text-gray-500 border-gray-200'
      }`}>
        "{theme.sampleQuestion}"
      </p>
    </div>
  );
};

// ─── Custom Tooltip for Reading Chart ────────────────────────────────────────

const ReadingTooltip: React.FC<any> = ({ active, payload, label }) => {
  const { isDarkMode } = useTheme();
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);

  return (
    <div className={`rounded-xl px-4 py-3 shadow-xl border ${
      isDarkMode ? 'bg-navy-800 border-white/10' : 'bg-white border-gray-200'
    }`}>
      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {label} &middot; {total.toFixed(1)}h total
      </p>
      {payload.map((p: any, i: number) => (
        p.value > 0 && (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{p.name}</span>
            <span className={`ml-auto font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{p.value}h</span>
          </div>
        )
      ))}
    </div>
  );
};

// ─── Main Student Detail Page ────────────────────────────────────────────────

const AnalyticsStudentDetail: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { instituteId, studentId } = useParams<{ instituteId: string; studentId: string }>();
  const navigate = useNavigate();

  const student = getStudentById(studentId || JASH_ID) || students[0];
  const isJash = student.id === JASH_ID;

  // For non-Jash students, we'd show a simpler view.
  // For the demo, Jash has full data. Others get a placeholder message.

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b ${isDarkMode ? 'border-white/10 bg-navy-800/50' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <button
            onClick={() => navigate(`/analytics/${instituteId}`)}
            className={`flex items-center gap-2 text-sm mb-3 ${
              isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            } transition-colors`}
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Cohort
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{student.name}</h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Physics Batch A &middot; NEET 2026
              </p>
            </div>
            {/* Quick stats */}
            <div className="flex gap-6">
              {[
                { label: 'Reading Hours', value: `${student.readingHours}h`, icon: <ClockIcon className="w-4 h-4" /> },
                { label: 'Questions', value: student.questionsAsked, icon: <ChatBubbleLeftRightIcon className="w-4 h-4" /> },
                { label: 'Highlights', value: student.highlights, icon: <LightBulbIcon className="w-4 h-4" /> },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className={`flex items-center justify-center gap-1 text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {stat.icon}
                    {stat.label}
                  </div>
                  <p className={`text-xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!isJash ? (
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
            Detailed analytics available for Jash Shah (demo student). Click back and select Jash Shah.
          </p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          {/* ─── 1. AI Learning Summary (TOP) ─────────────────────────── */}
          <Section title="Weekly Summary" icon={<SparklesIcon className="w-5 h-5" />}>
            {/* Struggle themes */}
            <div className="mb-5">
              <h4 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>Common Struggle Themes</h4>
              <div className="space-y-3">
                {jashAISummary.struggleThemes.map((t, i) => (
                  <div key={i} className={`rounded-xl p-4 ${
                    isDarkMode ? 'bg-red-500/5 border border-red-500/10' : 'bg-red-50 border border-red-100'
                  }`}>
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t.concept}
                    </span>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Study pattern */}
            <div className="mb-4">
              <h4 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>Study Pattern</h4>
              <ul className="space-y-1.5">
                {jashAISummary.studyPattern.map((line, i) => (
                  <li key={i} className={`text-sm flex items-start gap-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <span className="text-frost-500 mt-1">&#8226;</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            {/* Question evolution */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h4 className={`text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>How Questions Evolved</h4>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  isDarkMode ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600'
                }`}>AI-generated</span>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {jashAISummary.questionEvolution}
              </p>
            </div>
          </Section>

          {/* ─── 2. Reading Time This Week ──────────────────────────── */}
          <Section title="Reading Time This Week" icon={<ClockIcon className="w-5 h-5" />}>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={jashReadingTime} barCategoryGap="20%">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: isDarkMode ? '#9CA3AF' : '#6B7280', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: isDarkMode ? '#9CA3AF' : '#6B7280', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    unit="h"
                  />
                  <Tooltip content={<ReadingTooltip />} cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }} />
                  <Legend
                    iconType="square"
                    iconSize={10}
                    wrapperStyle={{ fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}
                  />
                  {jashSubjects.map(subject => (
                    <Bar
                      key={subject}
                      dataKey={subject}
                      stackId="a"
                      fill={subjectColors[subject]}
                      radius={subject === jashSubjects[jashSubjects.length - 1] ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Total this week: {jashReadingTime.reduce((sum, d) => sum + jashSubjects.reduce((s, sub) => s + ((d as any)[sub] || 0), 0), 0).toFixed(1)}h
            </p>
          </Section>

          {/* ─── 2. Struggle Themes This Week ──────────────────────── */}
          <Section
            title="Struggle Themes — This Week"
            icon={<ExclamationTriangleIcon className="w-5 h-5" />}
            badge={
              <span className={`text-xs px-2 py-1 rounded-full ${
                isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
              }`}>
                {jashStruggleThemes.filter(t => t.status === 'struggling').length} active struggles
              </span>
            }
          >
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Cross-referenced with memory review data. Mastered concepts are hidden.
            </p>
            <div className="space-y-3">
              {jashStruggleThemes.map((theme, i) => (
                <StruggleRow key={i} theme={theme} />
              ))}
            </div>
          </Section>

          {/* ─── 3. Overconfidence ─────────────────────────────────── */}
          <Section
            title="Overconfidence Alert"
            icon={<ExclamationTriangleIcon className="w-5 h-5" />}
            badge={
              <span className={`text-xs px-2 py-1 rounded-full ${
                jashOverconfidence.overconfidenceRate > 0.25
                  ? isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
                  : isDarkMode ? 'bg-yellow-500/10 text-yellow-400' : 'bg-yellow-50 text-yellow-600'
              }`}>
                {(jashOverconfidence.overconfidenceRate * 100).toFixed(0)}% overconfidence rate
              </span>
            }
          >
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Jash rated himself <strong>4+/5 confidence</strong> on {jashOverconfidence.totalReviews} self-reviews
              but scored <strong>'forgot'</strong> on {jashOverconfidence.overconfidenceEvents} of them.
              These are concepts where self-assessed confidence doesn't match recall ability.
            </p>
            <div className="space-y-2">
              {jashOverconfidence.concepts.map((c, i) => (
                <div key={i} className={`flex items-center justify-between px-4 py-3 rounded-xl ${
                  isDarkMode ? 'bg-navy-700/50' : 'bg-gray-50'
                }`}>
                  <div>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {c.concept}
                    </span>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Avg confidence: {c.confidenceAvg}/5 &middot; Overconfident {c.timesOverconfident}x
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    c.lastRating === 'forgot'
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    Last: {c.lastRating}
                  </span>
                </div>
              ))}
            </div>
            <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Based on {jashOverconfidence.totalReviews} self-rated reviews (Pulse Check).
            </p>
          </Section>

          {/* ─── 4. Top Stuck Concepts ─────────────────────────────── */}
          <Section title="Top Stuck Concepts" icon={<LightBulbIcon className="w-5 h-5" />}>
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Concepts where repetition isn't working — needs a different approach, not more review.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`text-xs uppercase tracking-wider ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    <th className="text-left px-4 py-2 font-medium">Concept</th>
                    <th className="text-left px-4 py-2 font-medium">Why it's stuck</th>
                    <th className="text-right px-4 py-2 font-medium">Reviews</th>
                    <th className="text-right px-4 py-2 font-medium">State</th>
                  </tr>
                </thead>
                <tbody>
                  {jashStuckConcepts.map((c, i) => (
                    <tr key={i} className={`${
                      isDarkMode ? 'border-t border-white/5' : 'border-t border-gray-100'
                    }`}>
                      <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {c.concept}
                      </td>
                      <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {c.reason}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {c.reps}x
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          c.currentState === 'relearning'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {c.currentState}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

        </div>
      )}
    </div>
  );
};

export default AnalyticsStudentDetail;
